import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Package, AlertTriangle, CheckCircle, Clock, Edit, Trash2, Users, DollarSign } from 'lucide-react';
import { useSoftwares, useDeleteSoftware } from '@/hooks/useSoftwares';
import { Software, StatusSoftware } from '@/types/software';
import { NewSoftwareDialog } from './NewSoftwareDialog';
import { EditSoftwareDialog } from './EditSoftwareDialog';
import { LicencasUsuariosTab } from './LicencasUsuariosTab';
import { BillingTab } from './BillingTab';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const statusConfig: Record<StatusSoftware, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  ativo: { label: 'Ativo', variant: 'default' },
  inativo: { label: 'Inativo', variant: 'secondary' },
  vencido: { label: 'Vencido', variant: 'destructive' },
  em_renovacao: { label: 'Em Renovação', variant: 'outline' },
};

const tipoLicenciamentoLabels: Record<string, string> = {
  perpetua: 'Perpétua',
  assinatura: 'Assinatura',
  por_usuario: 'Por Usuário',
  por_dispositivo: 'Por Dispositivo',
  volume: 'Volume',
};

export function SoftwareManagement() {
  const { data: softwares, isLoading } = useSoftwares();
  const deleteSoftware = useDeleteSoftware();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [editingSoftware, setEditingSoftware] = useState<Software | null>(null);
  const [deletingSoftware, setDeletingSoftware] = useState<Software | null>(null);

  const filteredSoftwares = softwares?.filter((software) => {
    const matchesSearch = software.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      software.versao?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || software.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  // Métricas
  const totalSoftwares = softwares?.length || 0;
  const totalLicencasContratadas = softwares?.reduce((acc, s) => acc + s.quantidade_contratada, 0) || 0;
  const totalLicencasEmUso = softwares?.reduce((acc, s) => acc + (s.licencas_em_uso || 0), 0) || 0;
  const softwaresVencendo = softwares?.filter((s) => {
    if (!s.data_vencimento) return false;
    const vencimento = new Date(s.data_vencimento);
    const hoje = new Date();
    const diasRestantes = Math.ceil((vencimento.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    return diasRestantes <= 30 && diasRestantes > 0;
  }).length || 0;

  const handleDelete = () => {
    if (deletingSoftware) {
      deleteSoftware.mutate(deletingSoftware.id);
      setDeletingSoftware(null);
    }
  };

  const formatCurrency = (value?: number | null) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="softwares" className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="softwares" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Softwares
          </TabsTrigger>
          <TabsTrigger value="licencas-usuarios" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Licenças por Usuário
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Billing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="softwares" className="space-y-6 mt-6">
          {/* Cards de Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Softwares</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalSoftwares}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Licenças Contratadas</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalLicencasContratadas}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Licenças em Uso</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalLicencasEmUso}</div>
                <p className="text-xs text-muted-foreground">
                  {totalLicencasContratadas > 0 
                    ? `${Math.round((totalLicencasEmUso / totalLicencasContratadas) * 100)}% de utilização`
                    : '0% de utilização'}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Vencendo em 30 dias</CardTitle>
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">{softwaresVencendo}</div>
              </CardContent>
            </Card>
          </div>

          {/* Filtros e Ações */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <CardTitle>Softwares e Licenças</CardTitle>
                <Button onClick={() => setIsNewDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Software
                </Button>
              </div>
            </CardHeader>
            <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou versão..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="ativo">Ativo</SelectItem>
                <SelectItem value="inativo">Inativo</SelectItem>
                <SelectItem value="vencido">Vencido</SelectItem>
                <SelectItem value="em_renovacao">Em Renovação</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : filteredSoftwares.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum software encontrado
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Versão</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-center">Contratadas</TableHead>
                    <TableHead className="text-center">Em Uso</TableHead>
                    <TableHead className="text-center">Disponíveis</TableHead>
                    <TableHead>Valor Unitário</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSoftwares.map((software) => (
                    <TableRow key={software.id}>
                      <TableCell className="font-medium">{software.nome}</TableCell>
                      <TableCell>{software.versao || '-'}</TableCell>
                      <TableCell>{tipoLicenciamentoLabels[software.tipo_licenciamento]}</TableCell>
                      <TableCell className="text-center">{software.quantidade_contratada}</TableCell>
                      <TableCell className="text-center">{software.licencas_em_uso || 0}</TableCell>
                      <TableCell className="text-center">{software.licencas_disponiveis || 0}</TableCell>
                      <TableCell>{formatCurrency(software.valor_unitario)}</TableCell>
                      <TableCell>
                        {software.data_vencimento 
                          ? new Date(software.data_vencimento).toLocaleDateString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusConfig[software.status].variant}>
                          {statusConfig[software.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingSoftware(software)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingSoftware(software)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="licencas-usuarios" className="mt-6">
          <LicencasUsuariosTab />
        </TabsContent>

        <TabsContent value="billing" className="mt-6">
          <BillingTab />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <NewSoftwareDialog
        open={isNewDialogOpen} 
        onOpenChange={setIsNewDialogOpen} 
      />
      
      {editingSoftware && (
        <EditSoftwareDialog
          software={editingSoftware}
          open={!!editingSoftware}
          onOpenChange={(open) => !open && setEditingSoftware(null)}
        />
      )}

      <AlertDialog open={!!deletingSoftware} onOpenChange={(open) => !open && setDeletingSoftware(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o software "{deletingSoftware?.nome}"? 
              Esta ação não pode ser desfeita e todas as licenças associadas serão excluídas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
